// Canonical color palette - single source of truth
// See: https://linear.app/plutto/issue/OPSENG-341

export type SemaphoreStatus = 'green' | 'yellow' | 'red' | 'no_data'

export interface SemaphoreColors {
  dot: string
  text: string
  cellBg: string
  cellText: string
  border: string
}

export const SEMAPHORE: Record<SemaphoreStatus, SemaphoreColors> = {
  green: {
    dot: 'bg-emerald-500',
    text: 'text-emerald-600',
    cellBg: 'bg-emerald-100',
    cellText: 'text-emerald-800',
    border: 'border-l-emerald-500',
  },
  yellow: {
    dot: 'bg-amber-500',
    text: 'text-amber-600',
    cellBg: 'bg-amber-100',
    cellText: 'text-amber-800',
    border: 'border-l-amber-400',
  },
  red: {
    dot: 'bg-rose-500',
    text: 'text-rose-600',
    cellBg: 'bg-rose-100',
    cellText: 'text-rose-800',
    border: 'border-l-rose-500',
  },
  no_data: {
    dot: 'bg-gray-300',
    text: 'text-gray-400',
    cellBg: 'bg-gray-100',
    cellText: 'text-gray-400',
    border: 'border-l-gray-300',
  },
}

// Hex equivalents for Chart.js and canvas contexts that cannot use Tailwind classes
export const SEMAPHORE_HEX: Record<SemaphoreStatus, string> = {
  green: '#10b981',   // emerald-500
  yellow: '#f59e0b',  // amber-500
  red: '#ef4444',     // red-500
  no_data: '#d1d5db', // gray-300
}

export const TREND_COLORS: Record<string, string> = {
  up: 'text-emerald-600',
  down: 'text-rose-600',
  stable: 'text-gray-500',
  new: 'text-indigo-700',
}

/**
 * Color de texto para una variación con signo (delta mes a mes, tendencia):
 * positiva o neutra en verde, negativa en rojo. Centraliza el ternario que
 * antes vivía inline en los componentes (convención: no hardcodear colores).
 */
export function textColorForVariation(value: number): string {
  return value >= 0 ? TREND_COLORS.up : TREND_COLORS.down
}

/**
 * Color de texto para un delta respecto a un objetivo/base: por encima en ámbar
 * (sobre-ejecución / alerta), por debajo en rojo.
 */
export function textColorForDelta(value: number): string {
  return value > 0 ? 'text-amber-600' : 'text-rose-600'
}

export const MONTHLY_DELTA = {
  positive: 'text-emerald-600',
  negative: 'text-rose-600',
  neutral: 'text-gray-400',
  missing: 'text-gray-400',
} as const

export const CHART_FAMILY_COLORS = [
  '#6366f1', '#ec4899', '#f59e0b', '#10b981', '#8b5cf6',
  '#ef4444', '#06b6d4', '#84cc16', '#f97316', '#14b8a6',
] as const

export const CHART_GLOBAL_COLOR = '#4338ca'

// Identidad de ambito: sirve para RECONOCER una familia (swatch de la card, fila
// de la tabla, linea del grafico), nunca para comunicar estado. Por eso ninguno
// de sus tonos es emerald/amber/rose - los del semaforo - ni el indigo de la
// linea global (CHART_GLOBAL_COLOR). Ciclica: con mas familias que colores se
// repite el ciclo, y el orden pone tonos alejados en posiciones vecinas.
// Validada por contraste sobre blanco (>= 3:1) y por pares distinguibles con
// las tres formas comunes de daltonismo.
export const FAMILY_IDENTITY_COLORS = [
  '#DB2777', // pink-600
  '#0891B2', // cyan-600
  '#8B5CF6', // violet-500
  '#2563EB', // blue-600
  '#C026D3', // fuchsia-600
  '#0D9488', // teal-600
  '#7E22CE', // purple-700
  '#475569', // slate-600
] as const

export function familyIdentityColor(index: number): string {
  const palette = FAMILY_IDENTITY_COLORS
  return palette[((index % palette.length) + palette.length) % palette.length]
}

// Panel tintado de la zona «Resumen del período»: la separa de la zona de KPIs,
// que va en blanco. Es identidad de seccion, no estado.
export const PERFORMANCE_ZONE = {
  summaryBg: 'bg-indigo-50/40',
  summaryBorder: 'border-indigo-100',
  eyebrow: 'text-indigo-800',
} as const

// Bandas de la escala de umbrales del hero (0 -> critico 85 -> meta 100 -> tope).
// Tonos suaves: la escala es el fondo sobre el que se lee el marcador.
export const THRESHOLD_SCALE = {
  red: 'bg-rose-100',
  yellow: 'bg-amber-100',
  green: 'bg-emerald-100',
  marker: 'bg-gray-900',
  provisionalMarker: 'border-amber-600',
} as const

// Vertical dashed marker for the period being viewed
export const CHART_MARKER_COLOR = '#9ca3af' // gray-400

export const FINANCIAL_CHART = {
  capexBudget: '#6366f1', // indigo-500
  opexBudget: '#8b5cf6',  // violet-500
  totalBudget: '#6366f1', // indigo-500 (fallback when no capex/opex split)
  actualSpend: '#10b981', // emerald-500
} as const

export const COCKPIT_CARDS: Record<string, { border: string; text: string }> = {
  my_contracts: { border: 'border-l-indigo-500', text: 'text-indigo-700' },
  contracts_in_red: { border: 'border-l-rose-500', text: 'text-rose-600' },
  pending_measurements: { border: 'border-l-amber-400', text: 'text-amber-600' },
  overdue_action_plans: { border: 'border-l-rose-400', text: 'text-rose-500' },
}

export const ACTION_PLAN_PRIORITY: Record<string, { bg: string; text: string }> = {
  low: { bg: 'bg-gray-100', text: 'text-gray-600' },
  medium: { bg: 'bg-amber-100', text: 'text-amber-700' },
  high: { bg: 'bg-orange-100', text: 'text-orange-700' },
  critical: { bg: 'bg-rose-100', text: 'text-rose-700' },
}

export const ACTION_PLAN_DUE = {
  overdue: { bg: 'bg-rose-100', text: 'text-rose-700', inline: 'text-rose-500' },
  upcoming: { bg: 'bg-amber-100', text: 'text-amber-700' },
} as const

export const ACTION_PLAN_ALERT = {
  icon: 'text-rose-500',
  countBg: 'bg-rose-100',
  countText: 'text-rose-700',
} as const

export const MEETING_MINUTE_STATUS: Record<string, { bg: string; text: string; dot: string }> = {
  published: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-400' },
  draft: { bg: 'bg-gray-100', text: 'text-gray-500', dot: 'bg-gray-300' },
}

export const COMMITMENT_STATUS: Record<string, { bg: string; text: string }> = {
  open: { bg: 'bg-amber-50', text: 'text-amber-700' },
  closed: { bg: 'bg-emerald-50', text: 'text-emerald-700' },
  draft: { bg: 'bg-gray-100', text: 'text-gray-600' },
}

// Badge completo (borde incluido) para el status de un plan de accion.
export const ACTION_PLAN_STATUS_BADGE: Record<string, { classes: string; label: string }> = {
  draft: { classes: `border-gray-200 ${COMMITMENT_STATUS.draft.bg} ${COMMITMENT_STATUS.draft.text}`, label: 'Borrador' },
  open: { classes: `border-amber-200 ${COMMITMENT_STATUS.open.bg} ${COMMITMENT_STATUS.open.text}`, label: 'Abierto' },
  closed: { classes: `border-emerald-200 ${COMMITMENT_STATUS.closed.bg} ${COMMITMENT_STATUS.closed.text}`, label: 'Completado' },
}

export const SIDE_BADGE: Record<'organization' | 'supplier', { bg: string; text: string; label: string; border: string; dot: string }> = {
  organization: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-100', dot: 'bg-indigo-500', label: 'Empresa' },
  supplier: { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-100', dot: 'bg-violet-500', label: 'Proveedor' },
}

export const NOTIFICATION_TYPE: Record<string, { text: string; bg: string }> = {
  score_red: { text: SEMAPHORE.red.text, bg: SEMAPHORE.red.cellBg },
  score_yellow: { text: SEMAPHORE.yellow.text, bg: SEMAPHORE.yellow.cellBg },
  measurement_missing: { text: 'text-indigo-700', bg: 'bg-indigo-50' },
  action_plan_suggested: { text: 'text-amber-600', bg: 'bg-amber-50' },
  action_plan_item_assigned: { text: 'text-indigo-600', bg: 'bg-indigo-50' },
  action_plan_assigned: { text: 'text-indigo-600', bg: 'bg-indigo-50' },
  action_plan_due_soon: { text: 'text-amber-600', bg: 'bg-amber-50' },
  action_plan_overdue: { text: 'text-rose-600', bg: 'bg-rose-50' },
  action_plan_closed: { text: 'text-emerald-600', bg: 'bg-emerald-50' },
  reajuste_pendiente: { text: ACTION_PLAN_DUE.overdue.inline, bg: ACTION_PLAN_DUE.overdue.bg },
  reajuste_aplicado: { text: 'text-emerald-600', bg: 'bg-emerald-50' },
}

export const OBJECTIVE_TYPE_COLORS: Record<string, { bg: string; text: string }> = {
  maximize: { bg: 'bg-emerald-50', text: 'text-emerald-700' },
  minimize: { bg: 'bg-amber-50', text: 'text-amber-700' },
  target: { bg: 'bg-indigo-50', text: 'text-indigo-700' },
}

export const ACTIVE_TOGGLE = {
  on: 'bg-emerald-500',
  off: 'bg-gray-300',
} as const

export const OVERDUE_BADGE = {
  bg: 'bg-rose-50',
  text: 'text-rose-600',
} as const

export const DETERIORATING = {
  icon: 'text-rose-500',
  badgeBg: 'bg-rose-100',
  badgeText: 'text-rose-700',
  indicator: 'text-rose-600',
} as const

// Indigo accent for decorative section/list icons in dashboard cockpit widgets
export const DASHBOARD_ACCENT_ICON = 'text-indigo-500' as const

export const FORM_FOCUS = {
  border: 'focus:border-indigo-700',
  outline: 'focus:outline-none',
  ring: 'focus:ring-1 focus:ring-indigo-700',
} as const

export const CHECKBOX_FOCUS = {
  ring: 'focus:ring-indigo-700',
} as const

export const TOGGLE_FOCUS = {
  outline: 'focus:outline-none',
  ring: 'focus:ring-2 focus:ring-indigo-700 focus:ring-offset-2',
} as const

export const FORM_FOCUS_WITHIN = {
  border: 'focus-within:border-indigo-700',
  ring: 'focus-within:ring-1 focus-within:ring-indigo-700',
} as const

export const FORM_ERROR = {
  text: 'text-rose-500',
  border: 'border-rose-500',
  inputBorder: 'border-rose-400',
  bg: 'bg-rose-50',
} as const

export const ALERT_BANNER = {
  bg: 'bg-rose-50',
  text: 'text-rose-700',
  border: 'border-rose-200',
  badgeBg: 'bg-rose-100',
} as const

export const SUCCESS_FEEDBACK = {
  text: 'text-emerald-500',
} as const

export const WARNING_BANNER = {
  border: 'border-amber-200',
  bg: 'bg-amber-50',
  icon: 'text-amber-600',
  text: 'text-amber-800',
  textAlt: 'text-amber-700',
} as const

export const WARNING_ICON = {
  text: 'text-amber-500',
} as const

export const SUCCESS_BANNER = {
  border: 'border-emerald-200',
  bg: 'bg-emerald-50',
  icon: 'text-emerald-600',
  text: 'text-emerald-800',
  badgeBg: 'bg-emerald-100',
  badgeText: 'text-emerald-700',
  buttonBg: 'bg-emerald-600',
  buttonHover: 'hover:bg-emerald-700',
} as const

export const CONFIRMATION_BANNER = {
  border: 'border-indigo-200',
  bg: 'bg-indigo-50',
  icon: 'text-indigo-700',
  text: 'text-indigo-800',
} as const

export const FILLED_DOT = {
  on: 'bg-emerald-400',
  off: 'bg-gray-300',
} as const

export const PROGRESS_BAR = {
  completed: 'bg-emerald-500',
  inProgress: 'bg-indigo-500',
  empty: 'bg-gray-300',
} as const

export const ACTION_ICON = {
  base: 'inline-flex items-center justify-center min-w-[44px] min-h-[44px] rounded p-2 text-gray-400 hover:bg-gray-100',
  edit: 'hover:text-indigo-800',
  delete: 'hover:text-rose-500',
  move: 'hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-30',
} as const

export const UNREAD_DOT = {
  bg: 'bg-indigo-700',
} as const

export const NOTIFICATION_UNREAD = {
  border: 'border-indigo-200',
  bg: 'bg-indigo-50/30',
  text: 'text-indigo-800',
} as const

export const STEP_INDICATOR = {
  active: {
    buttonBg: 'bg-indigo-50',
    buttonText: 'text-indigo-700',
    dot: 'bg-indigo-700',
    icon: 'text-indigo-500',
  },
  inactive: {
    dot: 'bg-gray-200',
    dotText: 'text-gray-500',
    icon: 'text-gray-400',
  },
} as const

export const DANGER_BUTTON = {
  bg: 'bg-rose-600',
  hover: 'hover:bg-rose-700',
} as const

export const SUCCESS_BUTTON = {
  border: 'border-emerald-500',
  bg: 'bg-emerald-50',
  text: 'text-emerald-700',
  hover: 'hover:bg-emerald-100',
} as const

export const LINK_DANGER = {
  text: 'text-rose-600',
  hover: 'hover:text-rose-700',
} as const

export const CONTRACT_STATUS: Record<string, { bg: string; text: string }> = {
  active: { bg: 'bg-emerald-100', text: 'text-emerald-700' },
  inactive: { bg: 'bg-gray-100', text: 'text-gray-600' },
  closed: { bg: 'bg-rose-100', text: 'text-rose-700' },
}

export const LINK_COLOR = {
  text: 'text-indigo-700',
  hover: 'hover:text-indigo-800',
} as const

export const BADGE_NEUTRAL = {
  bg: 'bg-gray-100',
  text: 'text-gray-700',
} as const

// "sin datos" y textos de estado apagados con contraste AA (D6). SEMAPHORE.no_data.text
// es gray-400 (tono decorativo de punto) y falla AA como texto de cuerpo.
export const NO_DATA_TEXT = 'text-gray-600' as const

export const BADGE_PRIMARY = {
  bg: 'bg-indigo-100',
  text: 'text-indigo-800',
} as const

export const BADGE_DANGER = {
  bg: 'bg-rose-100',
  text: 'text-rose-800',
} as const

// Lower-contrast indigo badge, for when a primary badge sits next to BADGE_PRIMARY
// ones and must not compete with them (e.g. the tipo chip beside the variable chip).
export const BADGE_PRIMARY_SOFT = {
  bg: 'bg-indigo-50',
  text: 'text-indigo-700',
} as const

// Highlight background for the currently selected row/item in master-detail lists
// and tables (e.g. DataTable's selectedRowId, FamilyKpiTable expanded KPI).
export const SELECTED_ROW = {
  bg: 'bg-indigo-50',
} as const

// Full-screen branded backdrop for auth callback screens (AuthCallbackView,
// PortalAuthCallbackView): the indigo gradient and its muted subtitle text.
export const AUTH_SCREEN = {
  gradient: 'bg-gradient-to-br from-indigo-700 to-gray-900',
  subtitle: 'text-indigo-200',
} as const

export const CHECKBOX_ACCENT = {
  text: 'text-indigo-700',
  focusRing: 'focus:ring-indigo-700',
} as const

export const TAB_ACTIVE = {
  border: 'border-indigo-700',
  text: 'text-indigo-700',
} as const

// Attendance icon colors for meeting participant lists
export const ATTENDANCE_ICON = {
  present: 'text-emerald-500',
  absent: 'text-gray-300',
  unknown: 'text-gray-200',
  presentLabel: 'text-emerald-600',
  absentLabel: 'text-gray-400',
} as const

// Status icon colors for action plan rows in list views (check/radio icons)
export const ACTION_PLAN_STATUS_ICON = {
  closed: { icon: 'text-emerald-500', title: 'text-gray-400' },
  open: { icon: 'text-gray-300', title: 'text-gray-800' },
} as const

// Status icon colors for individual action items within a plan detail
export const ACTION_ITEM_STATUS = {
  completed: { icon: 'text-emerald-400' },
  pending: { icon: 'text-gray-300' },
} as const

// Dots used next to critical / target threshold values in KPI rows
export const KPI_THRESHOLD = {
  critical: 'bg-rose-400',
  target: 'bg-emerald-400',
} as const

// Badge for manually overridden formula KPIs
export const OVERRIDE_BADGE = {
  bg: 'bg-indigo-50',
  text: 'text-indigo-700',
  border: 'border-indigo-200',
} as const

// Lighter bar color for overridden measurements in charts
export const CHART_OVERRIDE_COLOR = '#a5b4fc' // indigo-300

// PluttoButton style classes - single source of truth for shared button colors
export const BUTTON_COLORS: Record<string, string> = {
  primary: 'bg-indigo-700 text-white hover:bg-indigo-800',
  secondary: 'border border-gray-200 text-gray-700 hover:bg-gray-50',
  danger: `${DANGER_BUTTON.bg} text-white ${DANGER_BUTTON.hover}`,
  success: `border ${SUCCESS_BUTTON.border} ${SUCCESS_BUTTON.bg} ${SUCCESS_BUTTON.text} ${SUCCESS_BUTTON.hover}`,
  link: `${LINK_COLOR.text} ${LINK_COLOR.hover}`,
  'link-danger': `${LINK_DANGER.text} ${LINK_DANGER.hover}`,
  'primary-outlined': 'border border-indigo-700 text-indigo-700 hover:bg-indigo-50',
  ghost: 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 active:text-gray-900 active:bg-gray-200',
  'ghost-primary': 'text-indigo-600 hover:text-indigo-700 hover:bg-indigo-100 active:text-indigo-900 active:bg-indigo-200',
  soft: 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors',
  'danger-outlined': 'border border-red-500 text-red-500 hover:bg-red-50',
  'danger-borderless': 'border border-transparent text-red-500 hover:text-red-600 hover:bg-red-50',
}

export const INFORMATIVE_BADGE = 'bg-gray-100 text-gray-600' as const

export const MANAGEMENT_REPORT_UI = {
  presentation: 'bg-gray-950 text-gray-100',
  link: 'text-indigo-700',
  informativeBadge: INFORMATIVE_BADGE,
  metricPanel: 'bg-indigo-50 border-indigo-200',
  citation: 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100',
  focus: 'focus-visible:outline-2 focus-visible:outline-indigo-600',
  formFooter: 'bg-white border-gray-200 shadow-sm',
  surface: 'bg-white',
  heading: 'text-gray-900',
  text: 'text-gray-700',
  muted: 'text-gray-500',
  border: 'border-gray-200',
  divider: 'divide-gray-200',
  table: '[&_thead]:bg-gray-50',
} as const

export const MANAGEMENT_REPORT_STATUS = {
  generating: BADGE_PRIMARY_SOFT,
  ready: BADGE_NEUTRAL,
  failed: BADGE_DANGER,
} as const

export const TABLE_HEADER_FILTER = {
  active: 'text-indigo-700',
  inactive: 'text-gray-400',
} as const
